import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { Copy, RefreshCw, Loader2, Code, List, ChevronDown, ChevronRight, Upload } from 'lucide-react';
import { formatDT } from '../../utils/timezone';
import { exportRawPacketsHtml } from '../../utils/tripExport';

export function RawPacketViewer({ imei, api }) {
  const [packets, setPackets] = useState([]);
  const [ioMap, setIoMap] = useState({});
  const [protocol, setProtocol] = useState('');
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState('10');
  const [viewMode, setViewMode] = useState('summary');
  const [expandedPackets, setExpandedPackets] = useState({});

  const fetchPackets = useCallback(async () => {
    if (!imei) return;
    setLoading(true);
    try {
      const res = await api.get(`/parsing/devices/${imei}/raw-packets?limit=${limit}`);
      setPackets(res.data.packets || []);
      setIoMap(res.data.io_map || {});
      setProtocol(res.data.protocol || '');
    } catch (err) {
      console.error('Failed to fetch raw packets:', err);
      toast.error('Failed to load raw packets');
    } finally {
      setLoading(false);
    }
  }, [api, imei, limit]);

  const toggleExpand = (id) => {
    setExpandedPackets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyHex = (hex) => {
    navigator.clipboard.writeText(hex);
    toast.success('Raw hex copied to clipboard');
  };

  const getIOLabel = (key) => {
    const label = ioMap[key];
    if (label && typeof label === 'object') return key;
    return label || key;
  };

  const getEventName = (eventId) => {
    const events = ioMap._events || ioMap._log_reasons || {};
    return events[String(eventId)] || `Event ${eventId}`;
  };

  // Meitrack CCE packets carry two event fields:
  //   • header ``event_io_id`` (IO tag 0x01) — almost always 0 on CCE
  //   • ``event_code_ext`` IO element (IO tag 0x40) — the real event code
  //     that drives the Rules Engine (78=Impact, 663=video event, etc.)
  // Show the extended code when it exists so the Raw Packet Viewer matches
  // what the device actually reported and what the notification used.
  const resolveDisplayEventCode = (pkt) => {
    const ext = pkt?.io_elements?.event_code_ext;
    if (ext != null && ext !== '' && Number(ext) !== 0) {
      return Number(ext);
    }
    return pkt?.event_id != null ? Number(pkt.event_id) : null;
  };

  const formatHex = (hex) => {
    if (!hex) return '';
    return hex.toUpperCase().match(/.{1,2}/g)?.join(' ') || hex.toUpperCase();
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    return formatDT(ts, { seconds: true });
  };

  const downloadBlob = (content, mime, ext) => {
    const blob = new Blob(['\uFEFF' + content], { type: `${mime};charset=utf-8;` });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `raw_packets_${imei}_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const exportPackets = (format) => {
    if (!packets.length) {
      toast.error('Load packets before exporting');
      return;
    }
    if (format === 'json') {
      downloadBlob(JSON.stringify({ imei, protocol, exported_at: new Date().toISOString(), packet_count: packets.length, packets }, null, 2), 'application/json', 'json');
    } else if (format === 'html') {
      const flattenIos = (ios) => Object.entries(ios || {}).sort(([a], [b]) => a.localeCompare(b)).flatMap(([k, v]) => {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          return Object.entries(v).map(([sk, sv]) => [`${k}.${sk}`, getIOLabel(sk), typeof sv === 'boolean' ? (sv ? 'Yes' : 'No') : String(sv)]);
        }
        if (Array.isArray(v)) return [[k, getIOLabel(k), `${v.length} items`]];
        return [[k, getIOLabel(k), typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)]];
      });
      const items = packets.map((pkt, i) => {
        const code = resolveDisplayEventCode(pkt);
        return {
          index: i + 1,
          time: formatTimestamp(pkt.timestamp),
          serverTime: formatTimestamp(pkt.server_timestamp),
          protocol: pkt.protocol || '',
          codec: pkt.codec || '',
          event: code != null && code !== 0 ? `${getEventName(code)}` : '',
          speed: pkt.gps?.speed ?? 0,
          heading: pkt.gps?.angle ?? 0,
          altitude: pkt.gps?.altitude ?? 0,
          satellites: pkt.gps?.satellites ?? 0,
          position: pkt.gps?.latitude != null ? `${pkt.gps.latitude.toFixed(6)}, ${pkt.gps.longitude?.toFixed(6)}` : '',
          bytes: pkt.raw_hex ? Math.ceil(pkt.raw_hex.length / 2) : 0,
          rawHex: formatHex(pkt.raw_hex),
          ios: flattenIos(pkt.io_elements),
        };
      });
      exportRawPacketsHtml(
        'Raw Packet Viewer',
        `IMEI ${imei}${protocol ? ` · ${protocol}` : ''} · ${packets.length} packets`,
        items,
        `raw_packets_${imei}_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.html`,
      ).catch(() => toast.error('Export failed'));
    } else {
      const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = ['#', 'Timestamp', 'Server Time', 'Protocol', 'Codec', 'Event', 'Latitude', 'Longitude', 'Speed (km/h)', 'Heading', 'Altitude (m)', 'Satellites', 'IO Elements', 'Raw Hex'];
      const rows = packets.map((pkt, i) => {
        const code = resolveDisplayEventCode(pkt);
        const ios = Object.entries(pkt.io_elements || {})
          .map(([k, v]) => `${getIOLabel(k)}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
          .join('; ');
        return [
          i + 1,
          formatTimestamp(pkt.timestamp),
          formatTimestamp(pkt.server_timestamp),
          pkt.protocol || '',
          pkt.codec || '',
          code != null && code !== 0 ? `${code} (${getEventName(code)})` : '',
          pkt.gps?.latitude ?? '',
          pkt.gps?.longitude ?? '',
          pkt.gps?.speed ?? '',
          pkt.gps?.angle ?? '',
          pkt.gps?.altitude ?? '',
          pkt.gps?.satellites ?? '',
          ios,
          (pkt.raw_hex || '').toUpperCase(),
        ].map(esc).join(',');
      });
      downloadBlob([header.join(','), ...rows].join('\n'), 'text/csv', 'csv');
    }
    toast.success(`Exported ${packets.length} packet${packets.length === 1 ? '' : 's'}`);
  };

  const renderDecodedBeacons = (pkt, ioKey, idx) => {
    const scans = pkt.decoded_beacons?.[ioKey] || [];
    if (!scans.length) return null;
    const batteryText = (scan) => {
      if (scan.battery != null) return `${scan.battery_estimated ? '~' : ''}${scan.battery}%`;
      if (scan.low_battery === true) return 'LOW';
      if (scan.low_battery === false) return 'OK';
      return '-';
    };
    const batteryClass = (scan) => {
      if (scan.low_battery === true || (scan.battery != null && scan.battery <= 20)) return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-900';
      if (scan.battery != null || scan.low_battery === false) return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-900';
      return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-800';
    };
    return (
      <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/80 p-3 text-xs dark:border-emerald-900 dark:bg-emerald-950/20" data-testid={`raw-packet-decoded-beacons-${idx}-${ioKey}`}>
        <div className="mb-2 font-medium text-emerald-800 dark:text-emerald-200">
          Decoded Beacon Data ({scans.length})
        </div>
        <div className="space-y-2">
          {scans.map((scan, scanIdx) => (
            <div
              key={`${scan.beacon_id || scan.mac}-${scanIdx}`}
              className="rounded-md border border-emerald-200/80 bg-white/80 p-2.5 dark:border-emerald-900/70 dark:bg-slate-950/50"
              data-testid={`raw-packet-decoded-beacon-card-${idx}-${ioKey}-${scanIdx}`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="font-semibold text-emerald-800 dark:text-emerald-200">Beacon #{scanIdx + 1}</span>
                <span className="rounded border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                  {scan.protocol || 'beacon'}
                </span>
              </div>
              <div className="space-y-1.5">
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <span className="text-muted-foreground">Beacon ID</span>
                  <span className="font-mono break-all" data-testid={`raw-packet-decoded-beacon-id-${idx}-${ioKey}-${scanIdx}`}>{scan.beacon_id || scan.mac || 'Not sent by tracker'}</span>
                </div>
                {!scan.beacon_id && !scan.mac && (
                  <div className="grid grid-cols-[110px_1fr] gap-2">
                    <span className="text-muted-foreground">Setup Hint</span>
                    <span className="text-amber-700 dark:text-amber-200" data-testid={`raw-packet-decoded-beacon-id-hint-${idx}-${ioKey}-${scanIdx}`}>Advanced Beacon parameter 1 (Beacon ID) is not enabled/sent.</span>
                  </div>
                )}
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <span className="text-muted-foreground">RSSI</span>
                  <span className="font-mono" data-testid={`raw-packet-decoded-beacon-rssi-${idx}-${ioKey}-${scanIdx}`}>{scan.rssi} dBm</span>
                </div>
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <span className="text-muted-foreground">Battery Status</span>
                  <span
                    className={`inline-flex w-fit rounded border px-2 py-0.5 font-mono text-[11px] ${batteryClass(scan)}`}
                    data-testid={`raw-packet-decoded-beacon-battery-${idx}-${ioKey}-${scanIdx}`}
                  >
                    {batteryText(scan)}
                  </span>
                </div>
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <span className="text-muted-foreground">Battery Voltage</span>
                  <span className="font-mono" data-testid={`raw-packet-decoded-beacon-voltage-${idx}-${ioKey}-${scanIdx}`}>{scan.battery_voltage_mv != null ? `${scan.battery_voltage_mv} mV` : '-'}</span>
                </div>
                {scan.temperature_c != null && (
                  <div className="grid grid-cols-[110px_1fr] gap-2">
                    <span className="text-muted-foreground">Temperature</span>
                    <span className="font-mono" data-testid={`raw-packet-decoded-beacon-temperature-${idx}-${ioKey}-${scanIdx}`}>{scan.temperature_c}°C</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="min-w-0 text-sm flex items-center gap-2">
            <Code className="w-4 h-4" /> Raw Packet Viewer
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Select value={limit} onValueChange={setLimit}>
              <SelectTrigger className="w-20 h-7 text-xs" data-testid="raw-packet-limit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex rounded-md border border-border overflow-hidden">
              <button
                onClick={() => setViewMode('summary')}
                className={`px-2 py-1 text-xs transition-colors ${viewMode === 'summary' ? 'bg-[#2563eb] text-white' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid="raw-packet-summary-view"
              >
                <List className="w-3 h-3" />
              </button>
              <button
                onClick={() => setViewMode('hex')}
                className={`px-2 py-1 text-xs transition-colors ${viewMode === 'hex' ? 'bg-[#2563eb] text-white' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid="raw-packet-hex-view"
              >
                <Code className="w-3 h-3" />
              </button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={packets.length === 0}
                  data-testid="raw-packet-export-btn"
                >
                  <Upload className="w-3 h-3" />
                  <span className="ml-1">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => exportPackets('csv')} data-testid="raw-packet-export-csv">CSV</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => exportPackets('json')} data-testid="raw-packet-export-json">JSON</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => exportPackets('html')} data-testid="raw-packet-export-html">HTML</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={fetchPackets}
              disabled={loading}
              data-testid="raw-packet-refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              <span className="ml-1">Load</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {packets.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">
            {loading ? 'Loading...' : 'Click Load to fetch recent packets'}
          </p>
        ) : (
          <div className="space-y-2">
              {packets.map((pkt, idx) => (
              <div key={pkt.id || idx} className="min-w-0 border border-border rounded-md overflow-hidden">
                  {/* Packet Header */}
                  <button
                    onClick={() => toggleExpand(pkt.id || idx)}
                    className="w-full min-w-0 flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/50 transition-colors"
                    data-testid={`raw-packet-header-${idx}`}
                  >
                    {expandedPackets[pkt.id || idx] ? (
                      <ChevronDown className="w-3 h-3 shrink-0" />
                    ) : (
                      <ChevronRight className="w-3 h-3 shrink-0" />
                    )}
                    <span className="font-mono text-muted-foreground shrink-0">#{idx + 1}</span>
                    <span className="font-mono truncate">{formatTimestamp(pkt.server_timestamp)}</span>
                    <Badge variant="outline" className="text-[10px] px-1 py-0">{pkt.codec || pkt.protocol}</Badge>
                    {pkt.gps?.speed > 0 && (
                      <span className="text-muted-foreground">{pkt.gps.speed} km/h</span>
                    )}
                    {pkt.event_id != null && pkt.event_id !== 0 && (
                      <Badge className="text-[10px] px-1 py-0 bg-amber-600">{getEventName(pkt.event_id)}</Badge>
                    )}
                    {(() => {
                      const code = resolveDisplayEventCode(pkt);
                      if (code == null || code === 0) return null;
                      if (pkt.event_id != null && pkt.event_id !== 0) return null; // already shown above
                      return (
                        <Badge
                          className="text-[10px] px-1 py-0 bg-amber-600"
                          data-testid={`raw-packet-event-code-ext-badge-${idx}`}
                        >
                          {getEventName(code)}
                        </Badge>
                      );
                    })()}
                    {pkt.raw_hex && (
                      <span className="text-muted-foreground ml-auto shrink-0">{Math.ceil(pkt.raw_hex.length / 2)}B</span>
                    )}
                  </button>

                  {/* Expanded Content */}
                  {expandedPackets[pkt.id || idx] && (
                    <div className="border-t border-border">
                      {viewMode === 'summary' ? (
                        <div className="p-3 space-y-2">
                          {/* GPS Data */}
                          <div className="min-w-0 text-xs break-words">
                            <span className="font-semibold text-muted-foreground">GPS:</span>
                            <span className="font-mono ml-2 break-all">
                              {pkt.gps?.latitude?.toFixed(6)}, {pkt.gps?.longitude?.toFixed(6)}
                              {' | '}{pkt.gps?.speed || 0} km/h
                              {' | '}{pkt.gps?.angle || 0}°
                              {' | Alt: '}{pkt.gps?.altitude || 0}m
                              {' | Sat: '}{pkt.gps?.satellites || 0}
                            </span>
                          </div>
                          {/* IO Elements Table */}
                          {pkt.io_elements && Object.keys(pkt.io_elements).length > 0 && (
                            <table className="w-full table-fixed text-xs">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="text-left py-1 px-2 text-muted-foreground font-medium w-1/3">IO Key</th>
                                  <th className="text-left py-1 px-2 text-muted-foreground font-medium w-1/3">Label</th>
                                  <th className="text-left py-1 px-2 text-muted-foreground font-medium w-1/3">Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(pkt.io_elements).sort(([a], [b]) => a.localeCompare(b)).flatMap(([key, val]) => {
                                  if (val && typeof val === 'object' && !Array.isArray(val)) {
                                    return [
                                      <tr key={key} className="border-b border-border bg-muted/20">
                                        <td colSpan={3} className="py-1 px-2 font-semibold text-muted-foreground">{getIOLabel(key)}</td>
                                      </tr>,
                                      ...Object.entries(val).map(([sk, sv]) => (
                                        <tr key={`${key}.${sk}`} className="border-b border-border/50 hover:bg-muted/30">
                                        <td className="py-1 pl-5 pr-2 font-mono text-muted-foreground break-all">{sk}</td>
                                        <td className="py-1 px-2 break-words">{getIOLabel(sk)}</td>
                                        <td className="py-1 px-2 font-mono break-all">{typeof sv === 'boolean' ? (sv ? 'Yes' : 'No') : String(sv)}</td>
                                        </tr>
                                      )),
                                    ];
                                  }
                                  if (Array.isArray(val)) {
                                    return [(
                                      <tr key={key} className="border-b border-border/50 hover:bg-muted/30">
                                        <td className="py-1 px-2 font-mono text-muted-foreground break-all">{key}</td>
                                        <td className="py-1 px-2 break-words">{getIOLabel(key)}</td>
                                        <td className="py-1 px-2 font-mono break-all">{val.length} items</td>
                                      </tr>
                                    )];
                                  }
                                  return [(
                                    <tr key={key} className="border-b border-border/50 hover:bg-muted/30">
                                      <td className="py-1 px-2 font-mono text-muted-foreground break-all">{key}</td>
                                      <td className="py-1 px-2 break-words">{getIOLabel(key)}</td>
                                      <td className="py-1 px-2 font-mono break-all">
                                        {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}
                                        {renderDecodedBeacons(pkt, key, idx)}
                                      </td>
                                    </tr>
                                  )];
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ) : (
                        <div className="p-3 space-y-2">
                          {/* Raw Hex Display */}
                          {pkt.raw_hex ? (
                            <>
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-muted-foreground">
                                  Raw Packet ({Math.ceil(pkt.raw_hex.length / 2)} bytes)
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 text-xs"
                                  onClick={() => copyHex(pkt.raw_hex.toUpperCase())}
                                  data-testid={`copy-hex-${idx}`}
                                >
                                  <Copy className="w-3 h-3 mr-1" /> Copy Hex
                                </Button>
                              </div>
                              <pre className="bg-slate-950 text-green-400 p-3 rounded-md text-[11px] font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap break-all select-all">
                                {formatHex(pkt.raw_hex)}
                              </pre>
                            </>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">
                              No raw hex available (packet was recorded before raw capture was enabled)
                            </p>
                          )}
                          {/* Decoded Fields Below Hex */}
                          <div className="text-xs mt-2">
                            <span className="font-semibold text-muted-foreground">Decoded:</span>
                            <div className="mt-1 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
                              <span className="text-muted-foreground">Timestamp:</span>
                              <span className="font-mono break-all">{formatTimestamp(pkt.timestamp)}</span>
                              <span className="text-muted-foreground">Server Time:</span>
                              <span className="font-mono break-all">{formatTimestamp(pkt.server_timestamp)}</span>
                              <span className="text-muted-foreground">Codec:</span>
                              <span className="font-mono break-all">{pkt.codec}</span>
                              <span className="text-muted-foreground">Event:</span>
                              <span className="font-mono break-all" data-testid={`raw-packet-event-${idx}`}>
                                {(() => {
                                  const code = resolveDisplayEventCode(pkt);
                                  if (code == null) return '-';
                                  return `${code} (${getEventName(code)})`;
                                })()}
                              </span>
                              <span className="text-muted-foreground">Position:</span>
                              <span className="font-mono break-all">{pkt.gps?.latitude?.toFixed(6)}, {pkt.gps?.longitude?.toFixed(6)}</span>
                              <span className="text-muted-foreground">Speed / Heading:</span>
                              <span className="font-mono">{pkt.gps?.speed || 0} km/h / {pkt.gps?.angle || 0}°</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
        )}
      </CardContent>
    </Card>
  );
}
